import React, { useState } from 'react';
import { 
  Shield, Users, MapPin, Zap, Fuel, ShoppingBag, Calendar, Settings,
  TrendingUp, Database, Bell, FileText, Lock, Eye, EyeOff, Edit3,
  Trash2, Check, X, Plus, Search, Filter, Download, Upload, RefreshCw,
  AlertTriangle, CheckCircle, Clock, BarChart3, Activity, Globe, DollarSign,
  Award, Briefcase, MessageSquare, Image, Video, Mic, ChevronRight, Save,
  UserPlus, UserMinus, Ban, UnlockKeyhole, Star, Flag, Target, Handshake,
  TrendingDown, PieChart, Info, Mail, Phone, Building2, Coins
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { AdvertManagement } from './admin/AdvertManagement';
import { getFeatureFlags, setFeatureFlags, type FeatureFlags } from '../utils/feature-flags';

interface AdminControlPanelProps {
  userRole: 'admin' | 'subadmin';
  userName: string;
  onClose: () => void;
}

type AdminSection = 
  | 'overview'
  | 'users'
  | 'reports'
  | 'locations'
  | 'market-prices'
  | 'events'
  | 'partnerships'
  | 'ajo-circles'
  | 'adverts'
  | 'settings'
  | 'moderation'
  | 'analytics'
  | 'audit-logs';

interface AdminPermissions {
  canEditUsers: boolean;
  canDeleteUsers: boolean;
  canBanUsers: boolean;
  canEditReports: boolean;
  canDeleteReports: boolean;
  canVerifyReports: boolean;
  canEditPrices: boolean;
  canManagePartners: boolean;
  canChangeSettings: boolean;
  canViewAuditLogs: boolean;
  canManageAdmins: boolean;
  canAccessAnalytics: boolean;
}

const ADMIN_PERMISSIONS: AdminPermissions = {
  canEditUsers: true,
  canDeleteUsers: true,
  canBanUsers: true,
  canEditReports: true,
  canDeleteReports: true,
  canVerifyReports: true,
  canEditPrices: true,
  canManagePartners: true,
  canChangeSettings: true,
  canViewAuditLogs: true,
  canManageAdmins: true,
  canAccessAnalytics: true,
};

const SUBADMIN_PERMISSIONS: AdminPermissions = {
  canEditUsers: true,
  canDeleteUsers: false,
  canBanUsers: true,
  canEditReports: true,
  canDeleteReports: false,
  canVerifyReports: true,
  canEditPrices: true,
  canManagePartners: false,
  canChangeSettings: false,
  canViewAuditLogs: true,
  canManageAdmins: false,
  canAccessAnalytics: true,
};

export function AdminControlPanel({ userRole, userName, onClose }: AdminControlPanelProps) {
  const [activeSection, setActiveSection] = useState<AdminSection>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const permissions = userRole === 'admin' ? ADMIN_PERMISSIONS : SUBADMIN_PERMISSIONS;

  const sections = [
    { id: 'overview', label: 'Overview', icon: BarChart3, color: 'blue' },
    { id: 'users', label: 'Users Management', icon: Users, color: 'green', permission: permissions.canEditUsers },
    { id: 'reports', label: 'Reports Management', icon: FileText, color: 'orange', permission: permissions.canEditReports },
    { id: 'locations', label: 'Locations & Regions', icon: MapPin, color: 'red' },
    { id: 'market-prices', label: 'Market Prices', icon: ShoppingBag, color: 'green', permission: permissions.canEditPrices },
    { id: 'events', label: 'Events Management', icon: Calendar, color: 'purple' },
    { id: 'partnerships', label: 'Partnerships', icon: Handshake, color: 'indigo', permission: permissions.canManagePartners },
    { id: 'ajo-circles', label: 'Ajo Circles', icon: Coins, color: 'amber' },
    { id: 'adverts', label: 'Advertisement System', icon: BarChart3, color: 'pink', permission: permissions.canEditUsers },
    { id: 'moderation', label: 'Content Moderation', icon: Shield, color: 'red', permission: permissions.canVerifyReports },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp, color: 'blue', permission: permissions.canAccessAnalytics },
    { id: 'settings', label: 'App Settings', icon: Settings, color: 'gray', permission: permissions.canChangeSettings },
    { id: 'audit-logs', label: 'Audit Logs', icon: Clock, color: 'slate', permission: permissions.canViewAuditLogs },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex"
      >
        {/* Sidebar */}
        <div className="w-72 bg-gradient-to-b from-slate-900 to-slate-800 p-6 overflow-y-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Admin Panel</h3>
                <p className="text-slate-400 text-xs">
                  {userRole === 'admin' ? 'Full Access' : 'Limited Access'}
                </p>
              </div>
            </div>
            <div className="mt-4 px-3 py-2 bg-slate-800 rounded-lg">
              <p className="text-slate-300 text-sm font-medium">{userName}</p>
              <p className="text-slate-500 text-xs capitalize">{userRole}</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {sections
              .filter(section => section.permission !== false)
              .map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    data-section={section.id}
                    onClick={() => setActiveSection(section.id as AdminSection)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActive
                        ? 'bg-white text-slate-900'
                        : 'text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="text-sm font-medium">{section.label}</span>
                    {isActive && (
                      <ChevronRight size={16} className="ml-auto" />
                    )}
                  </button>
                );
              })}
          </nav>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full mt-8 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
          >
            <X size={18} />
            Close Panel
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Content Header */}
          <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-6 z-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">
                  {sections.find(s => s.id === activeSection)?.label}
                </h2>
                <p className="text-slate-600 text-sm mt-1">
                  Manage and update information across the platform
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2">
                  <Save size={18} />
                  Save Changes
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="mt-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search anything..."
                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-lg focus:border-green-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Content Area */}
          <div className="p-8">
            {activeSection === 'overview' && (
              <OverviewSection userRole={userRole} />
            )}
            {activeSection === 'users' && (
              <UsersManagementSection permissions={permissions} />
            )}
            {activeSection === 'reports' && (
              <ReportsManagementSection permissions={permissions} />
            )}
            {activeSection === 'locations' && (
              <LocationsManagementSection />
            )}
            {activeSection === 'market-prices' && (
              <MarketPricesSection permissions={permissions} />
            )}
            {activeSection === 'events' && (
              <EventsManagementSection />
            )}
            {activeSection === 'partnerships' && (
              <PartnershipsSection permissions={permissions} />
            )}
            {activeSection === 'ajo-circles' && (
              <AjoCirclesSection />
            )}
            {activeSection === 'moderation' && (
              <ModerationSection permissions={permissions} />
            )}
            {activeSection === 'analytics' && (
              <AnalyticsSection permissions={permissions} />
            )}
            {activeSection === 'settings' && (
              <SettingsSection permissions={permissions} />
            )}
            {activeSection === 'audit-logs' && (
              <AuditLogsSection permissions={permissions} />
            )}
            {activeSection === 'adverts' && (
              <AdvertManagement canManage={userRole === 'admin'} />
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Overview Section
function OverviewSection({ userRole }: { userRole: string }) {
  const stats = [
    { label: 'Total Users', value: '10,045', change: '+12%', icon: Users, color: 'blue' },
    { label: 'Active Reports', value: '2,137', change: '+8%', icon: FileText, color: 'orange' },
    { label: 'Market Prices', value: '856', change: '+3%', icon: ShoppingBag, color: 'green' },
    { label: 'Active Events', value: '124', change: '+15%', icon: Calendar, color: 'purple' },
    { label: 'Partnerships', value: '18', change: '+2', icon: Handshake, color: 'indigo' },
    { label: 'Ajo Circles', value: '342', change: '+22%', icon: Coins, color: 'amber' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg bg-${stat.color}-100 flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 text-${stat.color}-600`} />
                </div>
                <span className="text-green-600 text-sm font-semibold">{stat.change}</span>
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</h3>
              <p className="text-slate-600 text-sm">{stat.label}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-6 text-white">
        <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2">
            <UserPlus size={18} />
            Add User
          </button>
          <button className="bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2">
            <Plus size={18} />
            New Report
          </button>
          <button className="bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2">
            <Upload size={18} />
            Bulk Import
          </button>
          <button className="bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2">
            <Download size={18} />
            Export Data
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {[
            { action: 'User banned', user: 'Chidi K.', time: '2 mins ago', type: 'warning' },
            { action: 'Report verified', user: 'Amina B.', time: '15 mins ago', type: 'success' },
            { action: 'Price updated', user: 'Tunde O.', time: '1 hour ago', type: 'info' },
            { action: 'Partnership added', user: 'LASTMA', time: '3 hours ago', type: 'success' },
          ].map((activity, index) => (
            <div key={index} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${
                  activity.type === 'success' ? 'bg-green-500' :
                  activity.type === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                }`} />
                <div>
                  <p className="text-slate-900 font-medium">{activity.action}</p>
                  <p className="text-slate-500 text-sm">{activity.user}</p>
                </div>
              </div>
              <span className="text-slate-400 text-xs">{activity.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Users Management Section
function UsersManagementSection({ permissions }: { permissions: AdminPermissions }) {
  const [users, setUsers] = useState([
    { id: '1', name: 'Adebayo Johnson', email: 'adebayo@example.com', role: 'user', trustScore: 1250, status: 'active', location: 'Lagos' },
    { id: '2', name: 'Fatima Yusuf', email: 'fatima@example.com', role: 'user', trustScore: 890, status: 'active', location: 'Abuja' },
    { id: '3', name: 'Chidi Okonkwo', email: 'chidi@example.com', role: 'user', trustScore: 520, status: 'banned', location: 'Port Harcourt' },
  ]);

  const [editingUser, setEditingUser] = useState<string | null>(null);

  const handleEditUser = (userId: string) => {
    setEditingUser(userId);
  };

  const handleSaveUser = (userId: string) => {
    setEditingUser(null);
    toast.success('User Updated', { description: 'Changes saved successfully' });
  };

  const handleBanUser = (userId: string) => {
    if (!permissions.canBanUsers) {
      toast.error('Permission Denied', { description: 'You cannot ban users' });
      return;
    }
    setUsers(users.map(u => u.id === userId ? { ...u, status: 'banned' } : u));
    toast.success('User Banned', { description: 'User has been banned from the platform' });
  };

  const handleDeleteUser = (userId: string) => {
    if (!permissions.canDeleteUsers) {
      toast.error('Permission Denied', { description: 'You cannot delete users' });
      return;
    }
    setUsers(users.filter(u => u.id !== userId));
    toast.success('User Deleted', { description: 'User permanently removed' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900">All Users ({users.length})</h3>
        <div className="flex gap-3">
          <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2">
            <Filter size={18} />
            Filter
          </button>
          <button className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2">
            <UserPlus size={18} />
            Add User
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Trust Score</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Location</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-green-700 font-semibold">{user.name[0]}</span>
                      </div>
                      <div>
                        {editingUser === user.id ? (
                          <input
                            type="text"
                            defaultValue={user.name}
                            className="border border-slate-300 rounded px-2 py-1 text-sm"
                          />
                        ) : (
                          <p className="font-semibold text-slate-900">{user.name}</p>
                        )}
                        <p className="text-xs text-slate-500 capitalize">{user.role}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-700">
                    {editingUser === user.id ? (
                      <input
                        type="email"
                        defaultValue={user.email}
                        className="border border-slate-300 rounded px-2 py-1 text-sm w-full"
                      />
                    ) : (
                      user.email
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Star size={16} className="text-amber-500 fill-amber-500" />
                      <span className="font-semibold text-slate-900">{user.trustScore}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-700">{user.location}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      user.status === 'active' 
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {editingUser === user.id ? (
                        <>
                          <button
                            onClick={() => handleSaveUser(user.id)}
                            className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditUser(user.id)}
                            className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
                          >
                            <Edit3 size={16} />
                          </button>
                          {permissions.canBanUsers && (
                            <button
                              onClick={() => handleBanUser(user.id)}
                              className="p-2 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg transition-colors"
                            >
                              <Ban size={16} />
                            </button>
                          )}
                          {permissions.canDeleteUsers && (
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Reports Management Section (similar structure)
function ReportsManagementSection({ permissions }: { permissions: AdminPermissions }) {
  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Reports Management</h3>
        <p className="text-slate-600 mb-4">Edit, verify, or remove reports across all categories</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-red-50 rounded-lg">
            <MapPin className="w-6 h-6 text-red-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">1,256</p>
            <p className="text-sm text-slate-600">Traffic Reports</p>
          </div>
          <div className="p-4 bg-amber-50 rounded-lg">
            <Zap className="w-6 h-6 text-amber-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">432</p>
            <p className="text-sm text-slate-600">Power Reports</p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <Fuel className="w-6 h-6 text-blue-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">189</p>
            <p className="text-sm text-slate-600">Fuel Reports</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <ShoppingBag className="w-6 h-6 text-green-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">260</p>
            <p className="text-sm text-slate-600">Market Reports</p>
          </div>
        </div>
        {permissions.canEditReports && (
          <button className="mt-4 w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors">
            Manage All Reports →
          </button>
        )}
      </div>
    </div>
  );
}

// Placeholder sections (similar structure)
function LocationsManagementSection() {
  return <div className="bg-white border border-slate-200 rounded-xl p-6">
    <h3 className="text-lg font-bold mb-4">Locations & Regions Management</h3>
    <p className="text-slate-600">Update states, LGAs, and regional information</p>
  </div>;
}

function MarketPricesSection({ permissions }: { permissions: AdminPermissions }) {
  return <div className="bg-white border border-slate-200 rounded-xl p-6">
    <h3 className="text-lg font-bold mb-4">Market Prices Management</h3>
    <p className="text-slate-600">Update prices for all market basket items</p>
  </div>;
}

function EventsManagementSection() {
  return <div className="bg-white border border-slate-200 rounded-xl p-6">
    <h3 className="text-lg font-bold mb-4">Events Management</h3>
    <p className="text-slate-600">Create, edit, or remove community events</p>
  </div>;
}

function PartnershipsSection({ permissions }: { permissions: AdminPermissions }) {
  return <div className="bg-white border border-slate-200 rounded-xl p-6">
    <h3 className="text-lg font-bold mb-4">Partnership Management</h3>
    <p className="text-slate-600">Manage partnerships with government agencies and businesses</p>
  </div>;
}

function AjoCirclesSection() {
  const featureFlags = getFeatureFlags();
  const isEnabled = featureFlags.digitalAjo;

  return (
    <div className="space-y-6">
      {/* Feature Status Alert */}
      {!isEnabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-amber-900 mb-2">Digital Ajo Feature is Currently Disabled</h4>
              <p className="text-sm text-amber-800 mb-4">
                The Digital Ajo feature is currently deactivated and hidden from all users. 
                To enable this feature, go to <strong>App Settings → Feature Flags</strong> and toggle the Digital Ajo switch.
              </p>
              <button 
                onClick={() => {
                  // This will be handled by the parent component's section change
                  const settingsButton = document.querySelector('[data-section="settings"]') as HTMLButtonElement;
                  if (settingsButton) settingsButton.click();
                }}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition-colors text-sm"
              >
                Go to Feature Settings →
              </button>
            </div>
          </div>
        </div>
      )}

      {isEnabled && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-sm text-green-800">
              <strong>Digital Ajo is Active</strong> - Users can access savings circles throughout the platform
            </p>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-lg font-bold mb-4">Ajo Circles Management</h3>
        <p className="text-slate-600">Monitor and manage all Ajo savings circles</p>
      </div>
    </div>
  );
}

function ModerationSection({ permissions }: { permissions: AdminPermissions }) {
  return <div className="bg-white border border-slate-200 rounded-xl p-6">
    <h3 className="text-lg font-bold mb-4">Content Moderation</h3>
    <p className="text-slate-600">Review flagged content and moderate user activity</p>
  </div>;
}

function AnalyticsSection({ permissions }: { permissions: AdminPermissions }) {
  return <div className="bg-white border border-slate-200 rounded-xl p-6">
    <h3 className="text-lg font-bold mb-4">Analytics Dashboard</h3>
    <p className="text-slate-600">View detailed analytics and insights</p>
  </div>;
}

function SettingsSection({ permissions }: { permissions: AdminPermissions }) {
  const [featureFlags, setFeatureFlagsState] = React.useState<FeatureFlags>(getFeatureFlags());

  const handleToggleFeature = (feature: keyof FeatureFlags) => {
    const updated = {
      ...featureFlags,
      [feature]: !featureFlags[feature]
    };
    setFeatureFlagsState(updated);
    setFeatureFlags({ [feature]: !featureFlags[feature] });
    toast.success('Feature Updated', { 
      description: `${feature} has been ${updated[feature] ? 'enabled' : 'disabled'}` 
    });
  };

  return (
    <div className="space-y-6">
      {/* Feature Flags Section */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Feature Flags</h3>
            <p className="text-slate-600 text-sm mt-1">Enable or disable features across the entire platform</p>
          </div>
          <div className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
            ADMIN ONLY
          </div>
        </div>

        <div className="space-y-4">
          {/* Digital Ajo Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Coins className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Digital Ajo (Esusu) Circles</h4>
                <p className="text-sm text-slate-600">Trust-based savings circles for communities</p>
              </div>
            </div>
            <button
              onClick={() => handleToggleFeature('digitalAjo')}
              className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${ 
                featureFlags.digitalAjo ? 'bg-green-600' : 'bg-slate-300'
              }`}
            >
              <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${ 
                featureFlags.digitalAjo ? 'translate-x-7' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {/* Status indicator */}
          <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 rounded-lg border border-blue-200">
            <Info className="w-5 h-5 text-blue-600" />
            <p className="text-sm text-blue-800">
              {featureFlags.digitalAjo 
                ? 'Digital Ajo is currently enabled and visible to all users' 
                : 'Digital Ajo is currently disabled and hidden from all users'}
            </p>
          </div>

          {/* Info box */}
          <div className="p-4 bg-zinc-50 rounded-lg border border-zinc-200">
            <h5 className="font-semibold text-zinc-900 mb-2 text-sm">About Feature Flags</h5>
            <p className="text-xs text-zinc-600 leading-relaxed">
              Feature flags allow you to enable or disable features across the entire platform without deploying new code. 
              When a feature is disabled, it will be hidden from all users including the main navigation, feature menus, 
              and search results. Changes take effect immediately.
            </p>
          </div>
        </div>
      </div>

      {/* Other App Settings */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">General Settings</h3>
        <p className="text-slate-600">Configure app-wide settings and preferences</p>
      </div>
    </div>
  );
}

function AuditLogsSection({ permissions }: { permissions: AdminPermissions }) {
  return <div className="bg-white border border-slate-200 rounded-xl p-6">
    <h3 className="text-lg font-bold mb-4">Audit Logs</h3>
    <p className="text-slate-600">Track all admin actions and system changes</p>
  </div>;
}